import {
  Heading,
  Text,
  Button,
  Box,
  Icon,
  Image,
  IconButton,
} from '@everlywell/ui-kit';
import { CheckCircle } from '@phosphor-icons/react';
import DynamicIcon from 'components/DynamicIcon';
import EmailField from 'components/FormFields/EmailField';
import { usePrePurchaseQuizContext } from 'contexts/PrePurchaseQuiz/PrePurchaseQuizContext';
import { navigate } from 'gatsby';
import React, { useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import analytics from 'utils/analytics';
import { sendNotificationEvent } from 'utils/api/subscriptionsApi';
import { ANALYTICS } from 'utils/constants/analytics';
import { showFlash } from 'utils/domHelpers';

import mobileModalImage from '../../InitialQuizScreen/images/mobile-ppq-modal-images.png';
import desktopModalImage from '../../InitialQuizScreen/images/ppq-modal-images.png';

export type YouAreEligibleProps = {
  firstName: string;
};

export type PrePurchaseQuizEmailData = {
  email: string;
};

/**
 * This component adds the You Are Eligible screen the pre-purchase quiz modal.
 */

// TODO:
// - move content to contentful and fetch it from there
function YouAreEligible(props: YouAreEligibleProps): JSX.Element {
  const {
    toggleModal,
    resetState,
    personalData,
    weightData,
    bmi,
    questionsAndAnswers,
  } = usePrePurchaseQuizContext();
  const { register, watch, handleSubmit, formState, errors } = useFormContext();
  const { isSubmitting } = formState;

  const handleModalClose = () => {
    toggleModal(false);
    resetState();
  };

  const emailValue = watch('email');

  useEffect(() => {
    analytics.track({
      event: ANALYTICS.EVENTS.VIEWED_COMPONENT,
      data: {
        label: ANALYTICS.PRE_PURCHASE_QUIZ.LABEL,
        step: ANALYTICS.PRE_PURCHASE_QUIZ.STEPS.YOU_ARE_ELIGIBLE,
      },
    });
  }, []);

  const onSubmit = async () => {
    // We need to send the data in the shape of [{ question: string; answer: string | string[] }] to the API
    const transformedData = [];

    // Transform personal data
    if (personalData) {
      const personalDataEntries = Object.entries(personalData.personalData);
      const personalDataTransformed = personalDataEntries.map(
        ([key, value]) => ({
          question: key,
          answer: value,
        }),
      );
      transformedData.push(...personalDataTransformed);
    }

    // Transform weight data
    if (weightData) {
      const weightDataEntries = Object.entries(weightData);
      const weightDataTransformed = weightDataEntries.map(([key, value]) => ({
        question: key,
        answer: value,
      }));
      transformedData.push(...weightDataTransformed);
    }

    // Transform BMI. API expects answer to be a string, so we need to convert BMI number to a string
    if (bmi) {
      transformedData.push({ question: 'BMI', answer: bmi.toString() });
    }

    // questionsAndAnswers is already in the correct shape, we just need to push it to the transformedData array
    if (questionsAndAnswers) {
      transformedData.push(...questionsAndAnswers);
    }

    // Now you can use the transformedData to send to your API
    try {
      await sendNotificationEvent({
        event_type: 'newsletter-subscription',
        resource: {
          email: emailValue,
          data_fields: {
            quiz: transformedData,
            newsletter: 'wc-plus-pre-purchase-quiz',
          },
        },
      });

      trackButtonClick();

      // Store data in sessionStorage, to be used to pre-fill the checkout form
      sessionStorage.setItem(
        'wcPlusSignupFirstName',
        JSON.stringify(personalData?.personalData.firstName),
      );
      sessionStorage.setItem(
        'wcPlusSignupLastName',
        JSON.stringify(personalData?.personalData.lastName),
      );
      sessionStorage.setItem('wcPlusSignupEmail', emailValue);

      // Redirect to /weightloss-checkout/ after successful submission
      navigate('/weightloss-checkout/');
    } catch (error) {
      showFlash(
        'alert-error',
        `We’re sorry! There is an error processing your request at the moment. Please try again later`,
      );
    }
  };

  const trackButtonClick = () => {
    analytics.track({
      event: ANALYTICS.EVENTS.CLICKED_BUTTON,
      data: {
        label: ANALYTICS.PRE_PURCHASE_QUIZ.LABEL,
        step: ANALYTICS.PRE_PURCHASE_QUIZ.STEPS.YOU_ARE_ELIGIBLE,
        cta: 'Join Weight Care+',
      },
    });
  };

  return (
    <Box
      display="flex"
      flexDirection="column"
      alignItems="center"
      justifyContent={['flex-end', null, 'center']}
      backgroundColor="eggplant.base"
      textAlign="center"
      height="100vh"
      position="relative"
      paddingTop={[5, null, 0]}
    >
      <IconButton
        colorScheme="whiteCS"
        size="sm"
        aria-label="close"
        icon={<DynamicIcon icon="X" color="tints.uiGrey" />}
        onClick={handleModalClose}
        width="40px"
        height="40px"
        position="absolute"
        top="20px"
        right={['24px', null, '70px']}
      />
      <Box maxWidth={[null, '600px']} paddingX="5">
        <Icon
          as={CheckCircle}
          color="eggplant.lighter"
          boxSize="36px"
          weight="fill"
        />
        <Heading
          as="h1"
          size="xl"
          color="eggplant.wash"
          opacity="0.6"
          marginBottom="1"
        >
          Congrats, {personalData?.personalData.firstName}!
        </Heading>
        <Heading
          as="h2"
          size="xl"
          color="tints.white"
          marginBottom={['8', '10']}
        >
          You're eligible for the program!
        </Heading>
        <Text color="tints.white" marginBottom={['1', '2']}>
          Enter your email to stay in touch and to get started with Weight Care+
        </Text>
        <EmailField
          register={register}
          placeholder="janesmith@email.com"
          errors={errors}
        />
        <Button
          variant="primary"
          size={['wide', 'md']}
          colorScheme="whiteCS"
          marginTop={['8', '10']}
          marginBottom={8}
          onClick={handleSubmit(onSubmit)}
          isDisabled={!emailValue}
          isLoading={isSubmitting}
          // This is a fix to make the button clickable on desktop, even if the bottom image shifts and covers the button
          position="relative"
          zIndex="5"
        >
          Join Weight Care+
        </Button>
      </Box>

      <Image
        src={mobileModalImage}
        alt="Image of a hexagon pattern and a woman smiling"
        display={['block', null, 'none']} // Show this image on mobile. This is a temporary hack until we move the images to Contentful.
        maxWidth="100%"
      />
      <Image
        src={desktopModalImage}
        alt="Image of a hexagon pattern and a woman smiling"
        display={['none', null, 'block']} // Show this image on desktop. This is a temporary hack until we move the images to Contentful.
        maxWidth="850px"
        position="absolute"
        bottom="0"
      />
    </Box>
  );
}

export default YouAreEligible;
